import axios from "axios";
import miniToastr from "mini-toastr";
import Public from "./public";
import Private from "./private";
import { i18n } from "../i18n";
import authSession from "./authSession";
import { resolveToastMessage } from "../shared/toast";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;

function resolveI18nTranslator(i18nInstance) {
  const candidate =
    i18nInstance && i18nInstance.global ? i18nInstance.global : i18nInstance;

  return {
    te(path) {
      return !!(candidate && typeof candidate.te === "function" && candidate.te(path));
    },
    t(path) {
      if (candidate && typeof candidate.t === "function") {
        return candidate.t(path);
      }
      return path;
    }
  };
}

class API {
  constructor() {
    this.public = new Public();
    this.private = new Private(this);
    this.baseURL = axios.defaults.baseURL;

    this.consts = {
      Pda: {
        Own: "own",
        Rent: "rent",
        Purchase: "purchase",
        None: "none"
      },
      ShotLight: {
        Own: "own",
        Purchase: "purchase",
        None: "none"
      },
      Rank: {
        Novice: "novice",
        Experienced: "experienced",
        Veteran: "veteran",
        Master: "master",
        Legend: "legend"
      },
      Request: {
        Approved: "approved",
        Commented: "commented",
        Declined: "declined",
        PartiallyPaid: "partially_paid",
        Paid: "paid",
        None: "none"
      },
      PaymentType: {
        Cash: "cash",
        Combatant: "combatant",
        Free: "free",
        Cashless: "cashless",
        None: "none"
      },
      UserGroups: {
        Member: "member",
        GameMaster: "gamemaster",
        FractionLeader: "fraction_leader",
        Admin: "admin"
      },
      ItemTypes: {
        Suit: "suit",
        Artefact: "artefact",
        Quest: "quest",
        Other: "other"
      },
      ItemOwners: {
        Own: "own",
        Personal: "personal",
        Other: "other"
      },
      ItemPlaces: {
        Storage: "storage",
        Garage: "garage",
        Home: "home",
        Other: "other"
      },
      FirmwareTypes: {
        Unknown: "unknown",
        Artefact: "artefact",
        PdaSoul: "soul",
        Anomaly: "anomaly",
        Defence: "defence"
      },
      LocationTypes: {
        Main: "main",
        Quest: "quest",
        Other: "other"
      },
      LocationZones: {
        Red: "red",
        Yellow: "yellow",
        Green: "green",
        Blue: "blue"
      },
      ActionTypes: {
        Unknown: "unknown",
        Legend: "legend",
        LegendMaster: "legend_master",
        PassportPhoto: "passport_photo",
        Photos: "photos",
        CallSign: "callsign",
        Suit: "suit",
        Fraction: "fraction",
        NewRequest: "new_request"
      }
    };

    this.refreshPromise = null;
    this.lastNetworkFailureAt = 0;
    this.initInterceptors();
  }

  setSession(tokens, options) {
    authSession.setSession(tokens, options);
    const accessToken = authSession.getAccessToken();
    if (accessToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      return;
    }

    delete axios.defaults.headers.common.Authorization;
  }

  clearSession() {
    this.refreshPromise = null;
    authSession.clearSession();
    delete axios.defaults.headers.common.Authorization;
  }

  getAccessToken() {
    return authSession.getAccessToken();
  }

  getRefreshToken() {
    return authSession.getRefreshToken();
  }

  isAuthenticated() {
    return authSession.isAuthenticated();
  }

  getActions() {
    return authSession.getActions();
  }

  setActions(actions) {
    authSession.setActions(actions);
  }

  hasAction(actionName) {
    return authSession.hasAction(actionName);
  }

  isAuthExcludedRequest(url = "") {
    const normalized = String(url)
      .split("?")[0]
      .toLowerCase();
    return (
      normalized.endsWith("/login") ||
      normalized === "login" ||
      normalized.endsWith("/auth/refresh") ||
      normalized === "auth/refresh" ||
      normalized.endsWith("/auth/logout") ||
      normalized === "auth/logout"
    );
  }

  redirectToLogin() {
    if (window.location.hash !== "#/login") {
      window.location.replace("#/login");
    }
  }

  isNetworkUnavailableError(error) {
    return Boolean(
      error &&
        !error.response &&
        (error.request || error.message === "Network Error" || error.code === "ERR_NETWORK")
    );
  }

  notifyBackendUnavailable(error) {
    const now = Date.now();
    if (now - this.lastNetworkFailureAt < 2000) {
      return;
    }

    this.lastNetworkFailureAt = now;
    miniToastr.error(
      resolveToastMessage(error, "error", i18n),
      this.t("notifications.error", "Error")
    );
  }

  handleBackendUnavailable(error, originalRequest = {}) {
    this.clearSession();
    this.notifyBackendUnavailable(error);
    this.redirectToLogin();

    if (this.isAuthExcludedRequest(originalRequest.url)) {
      return Promise.reject(error);
    }

    return new Promise(() => {});
  }

  async refreshSession(refreshToken) {
    return axios.post(
      "/auth/refresh",
      { refresh_token: refreshToken },
      { __skipAuthHandling: true }
    );
  }

  async logout() {
    const refreshToken = this.getRefreshToken();
    try {
      if (refreshToken) {
        await axios.post(
          "/auth/logout",
          { refresh_token: refreshToken },
          { __skipAuthHandling: true }
        );
      }
    } catch (e) {
      // ignore API logout errors, local cleanup must always happen
    } finally {
      this.clearSession();
    }
  }

  initInterceptors() {
    axios.interceptors.request.use(config => {
      config.headers = config.headers || {};

      if (this.isAuthExcludedRequest(config.url)) {
        delete config.headers.Authorization;
        delete config.headers.authorization;
        return config;
      }

      const accessToken = this.getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    });

    axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config || {};
        const status = error.response && error.response.status;

        if (this.isNetworkUnavailableError(error)) {
          return this.handleBackendUnavailable(error, originalRequest);
        }

        if (originalRequest.__skipAuthHandling) {
          return Promise.reject(error);
        }

        if (status !== 401) {
          return Promise.reject(error);
        }

        if (
          this.isAuthExcludedRequest(originalRequest.url) ||
          originalRequest._retry
        ) {
          this.clearSession();
          this.redirectToLogin();
          return Promise.reject(error);
        }

        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
          await this.logout();
          this.redirectToLogin();
          return Promise.reject(error);
        }

        try {
          if (!this.refreshPromise) {
            this.refreshPromise = this.refreshSession(refreshToken)
              .then(resp => {
                if (!resp || !resp.data || !resp.data.access_token) {
                  throw new Error("Invalid refresh response");
                }
                this.setSession(resp.data, { merge: true });
                return resp.data.access_token;
              })
              .finally(() => {
                this.refreshPromise = null;
              });
          }

          const newAccessToken = await this.refreshPromise;
          originalRequest._retry = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          return axios(originalRequest);
        } catch (refreshError) {
          await this.logout();
          this.redirectToLogin();
          return Promise.reject(refreshError);
        }
      }
    );
  }

  t(path, fallback) {
    const translator = resolveI18nTranslator(i18n);
    return translator.te(path) ? translator.t(path) : fallback;
  }

  mapTitle(group, code, fallback = code) {
    return this.t(`enums.${group}.${code}`, fallback);
  }

  getRankTitle(rankCode) {
    return this.mapTitle("rank", rankCode);
  }

  getConfirmTitle(confirmCode) {
    return this.mapTitle("request", confirmCode);
  }

  getPaymentTypeTitle(paymentType) {
    return this.mapTitle("paymentType", paymentType);
  }

  getPdaVariantTitle(code) {
    return this.mapTitle("pda", code);
  }

  getItemTypeTitle(type) {
    return this.mapTitle("itemType", type);
  }

  getItemTypeVariants() {
    return Object.values(this.consts.ItemTypes).map(value => ({
      text: this.getItemTypeTitle(value),
      value
    }));
  }

  getItemOwnersTitle(type) {
    return this.mapTitle("itemOwner", type);
  }

  getItemOwnersVariants() {
    return Object.values(this.consts.ItemOwners).map(value => ({
      text: this.getItemOwnersTitle(value),
      value
    }));
  }

  getItemPlacesTitle(type) {
    return this.mapTitle("itemPlace", type);
  }

  getItemPlacesVariants() {
    return Object.values(this.consts.ItemPlaces).map(value => ({
      text: this.getItemPlacesTitle(value),
      value
    }));
  }

  getFirmwareTypeTitle(type) {
    return this.mapTitle("firmwareType", type);
  }

  getFirmwaresTypeVariants() {
    return Object.values(this.consts.FirmwareTypes).map(value => ({
      text: this.getFirmwareTypeTitle(value),
      value
    }));
  }

  getLocationTypeTitle(type) {
    return this.mapTitle("locationType", type);
  }

  getLocationTypeVariants() {
    return Object.values(this.consts.LocationTypes).map(value => ({
      text: this.getLocationTypeTitle(value),
      value
    }));
  }

  getLocationZoneVariants() {
    return Object.values(this.consts.LocationZones).map(value => ({
      text: this.mapTitle("locationZone", value),
      value
    }));
  }

  getActionTypeTitle(actionType) {
    return this.mapTitle("actionType", actionType);
  }

  getActionTypeMessage(actionType) {
    return this.mapTitle("actionMessage", actionType);
  }

  getCommonYesNo(value) {
    return value ? this.t("common.yes", "Yes") : this.t("common.no", "No");
  }

  login(data) {
    this.clearSession();
    return axios
      .post("login", data)
      .then(res => {
        this.setSession(res.data);
        return this.private.getUserActions();
      })
      .then(res => {
        this.setActions(res.data);
      })
      .catch(error => {
        this.clearSession();
        return Promise.reject(error);
      });
  }

  restore(data) {
    return axios.post("restore", data);
  }

  changePassword(data) {
    return axios.post("users/passchange", data);
  }
}

export default new API();
